import React, { useState, useCallback } from "react";
import {
  FormControlLabel,
  Checkbox,
  Card,
  Stack,
  Button,
  IconButton,
} from "@mui/material";
import Div from "@jumbo/shared/Div";
import RestoreIcon from "@mui/icons-material/Restore";
import CodeIcon from "@mui/icons-material/Code";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { Panel } from "react-resizable-panels";
import useJumboTheme from "@jumbo/hooks/useJumboTheme";
import Editor from "@monaco-editor/react";
import { StyledTabs, StyledTab } from "components/CustomTabs";

const EvaluationEditor = ({
  problemData,
  selectedLanguage,
  evalCode,
  setEvalCode,
  editorLanguage,
  setProblemData,
}) => {
  const { theme } = useJumboTheme();
  const [isPanelOpen, setIsPanelOpen] = useState(true);

  // Toggle panel visibility
  const togglePanel = useCallback(() => setIsPanelOpen((prev) => !prev), []);

  // Reset editor content to the template for the selected language
  const handleReset = useCallback(() => {
    if (selectedLanguage) {
      const template = problemData?.problemEvaluationTemplates
        ?.filter((t) => t.active)
        .find((t) => t.programmingLanguage === selectedLanguage.key)?.template;
      setEvalCode(template || "");
    } else {
      setEvalCode("");
    }
  }, [selectedLanguage, problemData, setEvalCode]);

  // Get the active status for the selected language's template
  const isActive = selectedLanguage
    ? problemData?.problemEvaluationTemplates?.find(
        (t) => t.programmingLanguage === selectedLanguage.key
      )?.active ?? false
    : false;

  // Handle checkbox change to toggle active status
  const handleActiveChange = useCallback(
    (event) => {
      if (selectedLanguage) {
        setProblemData((prev) => {
          const templates = [...prev.problemEvaluationTemplates];
          const index = templates.findIndex(
            (t) => t.programmingLanguage === selectedLanguage.key
          );
          if (index !== -1) {
            templates[index] = {
              ...templates[index],
              active: event.target.checked,
            };
          } else {
            templates.push({
              template: evalCode || "",
              programmingLanguage: selectedLanguage.key,
              description: "",
              active: event.target.checked,
            });
          }
          return { ...prev, problemEvaluationTemplates: templates };
        });
      }
    },
    [selectedLanguage, evalCode, setProblemData]
  );

  return (
    <>
      {isPanelOpen ? (
        <Panel defaultSize={50} minSize={20}>
          <Card
            sx={{
              height: "100%",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              borderRadius: 0,
            }}
          >
            <Div
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
              }}
            >
              <StyledTabs
                value={0}
                sx={{ flexGrow: 1, display: "flex", gap: 6 }}
              >
                <StyledTab
                  icon={<CodeIcon />}
                  iconPosition="start"
                  label="Evaluation Code"
                />
              </StyledTabs>
              <FormControlLabel
                control={
                  <Checkbox
                    name="active"
                    checked={isActive}
                    onChange={handleActiveChange}
                    disabled={!selectedLanguage} // Disable if no language is selected
                  />
                }
                label="Active"
              />
              <Stack direction="row" spacing={2}>
                <Button
                  onClick={handleReset}
                  variant="contained"
                  endIcon={<RestoreIcon />}
                  size="small"
                  disabled={!selectedLanguage} // Enable if a language is selected
                >
                  Reset
                </Button>
              </Stack>
              <IconButton onClick={togglePanel}>
                <ChevronLeftIcon />
              </IconButton>
            </Div>
            <Div sx={{ flexGrow: 1, overflow: "auto" }}>
              <Editor
                key={`${editorLanguage}-${selectedLanguage?.key}`}
                height="100%"
                width="100%"
                theme={theme.mode === "dark" ? "vs-dark" : "light"}
                language={editorLanguage}
                value={evalCode}
                options={{
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                  automaticLayout: true,
                  wordWrap: "on",
                  folding: true,
                  renderWhitespace: "none",
                }}
                onChange={setEvalCode}
              />
            </Div>
          </Card>
        </Panel>
      ) : (
        <Card
          elevation={3}
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: 0,
          }}
        >
          <StyledTabs
            value={0}
            orientation="vertical"
            variant="scrollable"
            scrollButtons="auto"
          >
            <StyledTab
              icon={<CodeIcon />}
              iconPosition="start"
              label="Evaluation Code"
            />
          </StyledTabs>
          <IconButton onClick={togglePanel}>
            <ChevronRightIcon />
          </IconButton>
        </Card>
      )}
    </>
  );
};

export default EvaluationEditor;
